import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Modal, ReadOnlyGuard, SparkIcon, SparkModalPortal, TooltipTrigger, sparkToast, useReadOnly } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { getTaskTypeShortLabel } from '@/features/tasks/utils/types';
import { useAuth } from '@/features/auth/AuthContext';
import { getTaskTitle } from './taskSummary';
import {
    blockedDeployMenuLabel,
    undeployFirstTooltip,
} from '@/shared/utils/deploymentUx';

const deployedVersionLabelFor = (usecase) =>
    usecase?.deployed_version != null ? `V${Number(usecase.deployed_version)}` : null;

const submenuLabelStyle = { display: 'inline-block' };
const versionMenuItemStyle = { padding: '3px 16px', display: 'block' };
const subtaskSubmenuPopupClassName = 'task-subtask-submenu';

const subtaskSubmenuLabel = (usecase, style = {}) => (
    <span className="fs-unmask" style={{ ...submenuLabelStyle, ...style }}>
        {getTaskTypeShortLabel(usecase.task_type)}: {usecase.usecase_name}
    </span>
);

/** Shared submenu row shape used by Deploy / Edit / Delete subtask menus. */
const subtaskSubmenuItem = (usecase, keyPrefix, { label, ...rest } = {}) => ({
    key: `${keyPrefix}-${usecase._id}`,
    label: label ?? subtaskSubmenuLabel(usecase),
    ...rest,
});

/**
 * Mount a Spark confirm dialog on its own React root under document.body.
 *
 * Menu dropdowns (list-view TaskActionsDropdown, antd Dropdown) close on
 * outside mousedown and unmount their menu items. A portal rendered as a
 * child of those items is unmounted too — createPortal alone does not keep
 * React state alive. An independent root survives the menu closing so the
 * confirm action can still run `onConfirm`.
 */
export const openConfirmMenuDialog = ({
    title,
    body,
    onConfirm,
    primaryLabel = 'Yes',
    tertiaryLabel = 'No',
}) => {
    const host = document.createElement('div');
    host.setAttribute('data-confirm-menu-dialog', '');
    document.body.appendChild(host);
    const root = createRoot(host);

    const dispose = () => {
        root.unmount();
        host.remove();
    };

    root.render(
        <SparkModalPortal open onClose={dispose} size="text">
            <Modal
                feedbackType="warning"
                title={title}
                showSubtitle={false}
                showScrollBar={false}
                bodyVariant="text"
                danger
                primaryLabel={primaryLabel}
                tertiaryLabel={tertiaryLabel}
                onPrimaryClick={() => {
                    dispose();
                    onConfirm?.();
                }}
                onTertiaryClick={dispose}
                onCloseClick={dispose}
            >
                {body ? <p className="spark-modal-text-content">{body}</p> : null}
            </Modal>
        </SparkModalPortal>
    );
};

/**
 * Confirm-before-action wrapper for destructive menu-item labels. Replaces
 * the former antd Popconfirm: clicking the label opens a Spark confirmation
 * modal that outlives the parent dropdown closing, and runs `onConfirm`
 * only after the user accepts.
 */
export const ConfirmMenuLabel = ({
    title,
    body,
    onConfirm,
    primaryLabel,
    tertiaryLabel,
    children,
}) => (
    <ReadOnlyGuard placement="left">
        <span
            onClick={(e) => {
                e?.stopPropagation?.();
                openConfirmMenuDialog({ title, body, onConfirm, primaryLabel, tertiaryLabel });
            }}
        >
            {children}
        </span>
    </ReadOnlyGuard>
);

/**
 * Centralized hook for all "task" actions (Deploy/Undeploy with version
 * submenu, Edit, Duplicate, Delete) used by both TaskCard (Card View) and
 * TaskRow (List View). Owns:
 *   - the per-usecase deployable-versions cache,
 *   - the dropdown menu items array for the parent task,
 *   - a builder for per-subtask menu items (used in the List View
 *     when a composed task is expanded).
 *
 * Consumers should pass the `subGroup` (= one task) and the same callback
 * contract they pass to the Card View (onDeleteUsecase, onBulkDeleteUsecases,
 * onModelDeployment, onDuplicateUsecase, onEditTask). `onMoveTask` is
 * optional: when provided, a "Move Task" item opens the caller's move
 * destination picker (single-task variant of the bulk Move action).
 */
export const useTaskActions = ({
    subGroup,
    onDeleteUsecase,
    onBulkDeleteUsecases,
    onModelDeployment,
    onDuplicateUsecase,
    onEditTask,
    onMoveTask,
}) => {
    const { role } = useAuth();
    const isViewer = role === 'Viewer';
    const { readOnly } = useReadOnly();

    const usecases = subGroup?.usecases || [];
    const firstUsecase = usecases[0];
    const isMultiObjective = usecases.length > 1;
    const cardTitle = getTaskTitle(subGroup);

    const [availableVersions, setAvailableVersions] = useState({});
    const [loadingVersions, setLoadingVersions] = useState({});
    // Refs keep the fetch gate synchronous and stable across renders so
    // consumers that prefetch in useEffect (TaskPage) cannot re-fire the
    // request every time loading/available state flips. Checking
    // `versions.length > 0` alone re-looped forever when a usecase had no
    // completed trainings (empty array).
    const availableVersionsRef = useRef(availableVersions);
    const loadingVersionsRef = useRef(loadingVersions);
    availableVersionsRef.current = availableVersions;
    loadingVersionsRef.current = loadingVersions;

    const fetchAvailableVersions = useCallback((usecase) => {
        const usecaseId = usecase?._id;
        if (!usecaseId) return;
        if (
            loadingVersionsRef.current[usecaseId] ||
            Object.prototype.hasOwnProperty.call(availableVersionsRef.current, usecaseId)
        ) {
            return;
        }

        loadingVersionsRef.current = { ...loadingVersionsRef.current, [usecaseId]: true };
        setLoadingVersions((prev) => ({ ...prev, [usecaseId]: true }));
        data_from_server_post(
            'deployable-versions',
            {
                client_id: usecase.client_id,
                llm_usecase_id: usecaseId,
            },
            (result) => {
                loadingVersionsRef.current = {
                    ...loadingVersionsRef.current,
                    [usecaseId]: false,
                };
                setLoadingVersions((prev) => ({ ...prev, [usecaseId]: false }));
                // Always cache a result (including []) so empty/failed
                // responses do not retrigger the TaskPage prefetch effect.
                const versions =
                    result.success && Array.isArray(result.versions) ? result.versions : [];
                availableVersionsRef.current = {
                    ...availableVersionsRef.current,
                    [usecaseId]: versions,
                };
                setAvailableVersions((prev) => ({
                    ...prev,
                    [usecaseId]: versions,
                }));
                if (!(result.success && Array.isArray(result.versions))) {
                    sparkToast.error('Failed to load available versions');
                }
            }
        );
    }, []);

    const handleModelDeployment = useCallback(
        (usecase, version = null) => {
            if (!usecase?._id) return;
            const action = usecase.is_deployed ? 'undeploy' : 'deploy';
            onModelDeployment?.(usecase._id, action, version);
        },
        [onModelDeployment]
    );

    const getDeployVersionItems = useCallback(
        (usecase) => {
            const usecaseId = usecase?._id;
            const titleItem = {
                key: `model-version-title-${usecaseId}`,
                label: (
                    <div style={{ fontWeight: 300, color: '#8c8c8c', ...versionMenuItemStyle }}>
                        Model version
                    </div>
                ),
                disabled: true,
            };

            if (loadingVersions[usecaseId]) {
                return [
                    titleItem,
                    {
                        key: `loading-${usecaseId}`,
                        label: <div style={versionMenuItemStyle}>Loading versions...</div>,
                        disabled: true,
                    },
                ];
            }

            const versions = availableVersions[usecaseId] || [];
            if (versions.length === 0) {
                return [
                    titleItem,
                    {
                        key: `no-versions-${usecaseId}`,
                        label: <div style={versionMenuItemStyle}>No versions available</div>,
                        disabled: true,
                    },
                ];
            }

            return [
                titleItem,
                ...versions.map((version) => ({
                    key: `version-${usecaseId}-${version}`,
                    label: (
                        <div className="fs-unmask" style={versionMenuItemStyle}>
                            V{version}
                        </div>
                    ),
                    disabled: readOnly,
                    onClick: (e) => {
                        e?.domEvent?.stopPropagation?.();
                        e?.domEvent?.preventDefault?.();
                        handleModelDeployment(usecase, version);
                    },
                })),
            ];
        },
        [availableVersions, loadingVersions, handleModelDeployment, readOnly]
    );

    // All usecases the Deploy menu should list: non-deployed ones are
    // actionable; already-deployed ones show as disabled (undeploy first).
    const deployCandidateUsecases = useMemo(
        () => usecases.filter((uc) => !isGenerativeTask(uc?.task_type)),
        [usecases]
    );
    const deployableUsecases = useMemo(
        () => deployCandidateUsecases.filter((uc) => !uc?.is_deployed),
        [deployCandidateUsecases]
    );
    const undeployableUsecases = useMemo(
        () =>
            usecases.filter(
                (uc) => uc?.is_deployed && !isGenerativeTask(uc?.task_type)
            ),
        [usecases]
    );

    const handleDuplicateTask = useCallback(() => {
        if (!firstUsecase) return;
        onDuplicateUsecase?.({
            ...firstUsecase,
            usecase_name: cardTitle,
            sub_group_name: subGroup?.sub_group_name || cardTitle,
            // Raw stored goal — not `cardDescription`, whose "No task
            // description provided." display fallback would leak into the
            // Duplicate modal's description input as a real value.
            usecase_goal: firstUsecase?.usecase_goal || '',
            usecases,
        });
    }, [
        firstUsecase,
        cardTitle,
        subGroup,
        usecases,
        onDuplicateUsecase,
    ]);

    const handleDeleteTask = useCallback(async () => {
        if (onBulkDeleteUsecases) {
            await onBulkDeleteUsecases(usecases);
            return;
        }
        await Promise.all(
            usecases.map((uc) => onDeleteUsecase?.(uc.client_id, uc._id))
        );
    }, [usecases, onBulkDeleteUsecases, onDeleteUsecase]);

    const deployMenuItem = useMemo(() => {
        if (deployCandidateUsecases.length === 0 || isViewer) return null;

        if (!isMultiObjective) {
            // Already deployed with some version: keep Deploy visible but
            // disabled, with a tooltip explaining the undeploy-first rule.
            if (firstUsecase?.is_deployed) {
                return {
                    key: 'deploy-task',
                    label: blockedDeployMenuLabel(
                        'Deploy Task',
                        deployedVersionLabelFor(firstUsecase)
                    ),
                    icon: <SparkIcon slug="rocket" size="small" />,
                    disabled: true,
                };
            }
            return {
                key: 'deploy-task',
                label: <span className="fs-unmask">Deploy Task</span>,
                icon: <SparkIcon slug="rocket" size="small" />,
                children: getDeployVersionItems(firstUsecase),
                onTitleClick: () => fetchAvailableVersions(firstUsecase),
            };
        }

        return {
            key: 'deploy-task',
            label: <span className="fs-unmask">Deploy Task</span>,
            icon: <SparkIcon slug="rocket" size="small" />,
            popupClassName: subtaskSubmenuPopupClassName,
            children: deployCandidateUsecases.map((usecase) =>
                usecase?.is_deployed
                    ? subtaskSubmenuItem(usecase, 'deploy', {
                        disabled: true,
                        label: (
                            <TooltipTrigger
                                text={undeployFirstTooltip(deployedVersionLabelFor(usecase))}
                                placement="left"
                            >
                                {subtaskSubmenuLabel(usecase)}
                            </TooltipTrigger>
                        ),
                    })
                    : subtaskSubmenuItem(usecase, 'deploy', {
                        children: getDeployVersionItems(usecase),
                        onTitleClick: () => fetchAvailableVersions(usecase),
                    })
            ),
        };
    }, [
        deployCandidateUsecases,
        isViewer,
        isMultiObjective,
        firstUsecase,
        getDeployVersionItems,
        fetchAvailableVersions,
    ]);

    const undeployMenuItem = useMemo(() => {
        if (undeployableUsecases.length === 0 || isViewer) return null;

        if (!isMultiObjective) {
            return {
                key: 'undeploy-task',
                label: <span className="fs-unmask">Undeploy Task</span>,
                icon: <SparkIcon slug="circle-minus" size="small" />,
                disabled: readOnly,
                onClick: (e) => {
                    e?.domEvent?.stopPropagation?.();
                    handleModelDeployment(firstUsecase);
                },
            };
        }

        return {
            key: 'undeploy-task',
            label: <span className="fs-unmask">Undeploy Task</span>,
            icon: <SparkIcon slug="circle-minus" size="small" />,
            popupClassName: subtaskSubmenuPopupClassName,
            children: undeployableUsecases.map((usecase) =>
                subtaskSubmenuItem(usecase, 'undeploy', {
                    disabled: readOnly,
                    onClick: (e) => {
                        e?.domEvent?.stopPropagation?.();
                        handleModelDeployment(usecase);
                    },
                })
            ),
        };
    }, [
        undeployableUsecases,
        isViewer,
        isMultiObjective,
        firstUsecase,
        handleModelDeployment,
        readOnly,
    ]);

    const taskMenuItems = useMemo(() => {
        const items = [];
        if (deployMenuItem) items.push(deployMenuItem);
        if (undeployMenuItem) items.push(undeployMenuItem);

        if (!isViewer) {
            // "Edit Task details" always edits the umbrella (atomic: the
            // single usecase; multi: the shared `sub_group_name` across all
            // subtasks). Per-subtask rename for multi tasks lives in a
            // separate "Edit subtasks" sub-menu below.
            items.push({
                key: 'edit-task-details',
                label: 'Edit Task details',
                icon: <SparkIcon slug="edit" size="small" />,
                disabled: readOnly,
                onClick: (e) => {
                    e?.domEvent?.stopPropagation?.();
                    onEditTask?.(firstUsecase, 'umbrella');
                },
            });

            if (isMultiObjective) {
                items.push({
                    key: 'edit-subtasks',
                    label: 'Edit subtasks',
                    icon: <SparkIcon slug="edit" size="small" />,
                    popupClassName: subtaskSubmenuPopupClassName,
                    children: usecases.map((uc) =>
                        subtaskSubmenuItem(uc, 'edit', {
                            disabled: readOnly,
                            onClick: (e) => {
                                e?.domEvent?.stopPropagation?.();
                                onEditTask?.(uc, 'subtask');
                            },
                        })
                    ),
                });
            }

            items.push({
                key: 'duplicate-task',
                label: <span className="fs-unmask">Duplicate Task</span>,
                icon: <SparkIcon slug="copy" size="small" />,
                disabled: readOnly,
                onClick: (e) => {
                    e?.domEvent?.stopPropagation?.();
                    handleDuplicateTask();
                },
            });

            // Same position and icon as the task page kebab menu
            // (Duplicate → Move Task → destructive actions).
            if (onMoveTask) {
                items.push({
                    key: 'move-task',
                    label: <span className="fs-unmask">Move Task</span>,
                    icon: <SparkIcon slug="arrow-right" size="small" />,
                    disabled: readOnly,
                    onClick: (e) => {
                        e?.domEvent?.stopPropagation?.();
                        onMoveTask();
                    },
                });
            }

            if (isMultiObjective) {
                items.push({
                    key: 'delete-objective',
                    label: 'Delete subtask',
                    popupClassName: subtaskSubmenuPopupClassName,
                    children: usecases.map((uc) =>
                        subtaskSubmenuItem(uc, 'del', {
                            disabled: readOnly,
                            label: (
                                <ConfirmMenuLabel
                                    title={`Delete ${isMultiObjective ? 'objective' : 'task'
                                        } "${uc.usecase_name}"?`}
                                    onConfirm={() => {
                                        onDeleteUsecase?.(uc.client_id, uc._id);
                                    }}
                                >
                                    {subtaskSubmenuLabel(uc, {
                                        color: '#F87171',
                                        cursor: 'pointer',
                                    })}
                                </ConfirmMenuLabel>
                            ),
                        })
                    ),
                });
            }

            items.push({ type: 'divider' });
            items.push({
                key: 'delete-task',
                disabled: readOnly,
                icon: (
                    <span style={{ color: '#F87171', display: 'inline-flex' }}>
                        <SparkIcon slug="trash" size="small" />
                    </span>
                ),
                label: (
                    <ConfirmMenuLabel
                        title={`Delete task "${cardTitle}"?`}
                        body="All data, prompts, evaluations, and models associated with this task will be permanently deleted. This action can’t be undone."
                        primaryLabel="Delete Task"
                        tertiaryLabel="Cancel"
                        onConfirm={() => {
                            handleDeleteTask();
                        }}
                    >
                        <span className="fs-unmask" style={{ color: '#F87171' }}>
                            Delete Task
                        </span>
                    </ConfirmMenuLabel>
                ),
            });
        }

        return items;
    }, [
        deployMenuItem,
        undeployMenuItem,
        isViewer,
        isMultiObjective,
        usecases,
        firstUsecase,
        cardTitle,
        onEditTask,
        handleDuplicateTask,
        onMoveTask,
        onDeleteUsecase,
        handleDeleteTask,
        readOnly,
    ]);

    const getSubtaskMenuItems = useCallback(
        (usecase) => {
            if (!usecase) return [];
            const isDeployed = usecase.is_deployed;
            const taskType = usecase?.task_type;
            const isDeployable = !isDeployed && !isGenerativeTask(taskType);
            const isUndeployable = isDeployed && !isGenerativeTask(taskType);

            const items = [];

            if (!isViewer) {
                items.push({
                    key: `edit-subtask-${usecase._id}`,
                    label: <span className="fs-unmask">Edit subtask</span>,
                    icon: <SparkIcon slug="edit" size="small" />,
                    disabled: readOnly,
                    onClick: (e) => {
                        e?.domEvent?.stopPropagation?.();
                        onEditTask?.(usecase, 'subtask');
                    },
                });
            }

            if (isDeployable && !isViewer) {
                items.push({
                    key: `deploy-subtask-${usecase._id}`,
                    label: <span className="fs-unmask">Deploy Subtask</span>,
                    icon: <SparkIcon slug="rocket" size="small" />,
                    children: getDeployVersionItems(usecase),
                    onTitleClick: () => fetchAvailableVersions(usecase),
                });
            }

            if (isUndeployable && !isViewer) {
                // Deployed subtask: keep Deploy visible but disabled, with a
                // tooltip explaining the undeploy-first rule.
                items.push({
                    key: `deploy-subtask-${usecase._id}`,
                    label: blockedDeployMenuLabel(
                        'Deploy Subtask',
                        deployedVersionLabelFor(usecase)
                    ),
                    icon: <SparkIcon slug="rocket" size="small" />,
                    disabled: true,
                });
            }

            if (isUndeployable && !isViewer) {
                items.push({
                    key: `undeploy-subtask-${usecase._id}`,
                    label: <span className="fs-unmask">Undeploy Subtask</span>,
                    icon: <SparkIcon slug="circle-minus" size="small" />,
                    disabled: readOnly,
                    onClick: (e) => {
                        e?.domEvent?.stopPropagation?.();
                        handleModelDeployment(usecase);
                    },
                });
            }

            if (!isViewer) {
                items.push({ type: 'divider' });
                items.push({
                    key: `delete-subtask-${usecase._id}`,
                    disabled: readOnly,
                    icon: (
                        <span style={{ color: '#F87171', display: 'inline-flex' }}>
                            <SparkIcon slug="trash" size="small" />
                        </span>
                    ),
                    label: (
                        <ConfirmMenuLabel
                            title={`Delete subtask "${usecase.usecase_name}"?`}
                            onConfirm={() => {
                                onDeleteUsecase?.(usecase.client_id, usecase._id);
                            }}
                        >
                            <span className="fs-unmask" style={{ color: '#F87171' }}>
                                Delete Subtask
                            </span>
                        </ConfirmMenuLabel>
                    ),
                });
            }

            return items;
        },
        [
            isViewer,
            onEditTask,
            getDeployVersionItems,
            fetchAvailableVersions,
            handleModelDeployment,
            onDeleteUsecase,
            readOnly,
        ]
    );

    const onMenuOpenChange = useCallback(
        (open) => {
            if (!open) return;
            deployableUsecases.forEach(fetchAvailableVersions);
        },
        [deployableUsecases, fetchAvailableVersions]
    );

    return {
        taskMenuItems,
        getSubtaskMenuItems,
        onMenuOpenChange,
        fetchAvailableVersions,
        // Completed (deployable) versions per usecase id — populated by
        // fetchAvailableVersions. Lets surfaces gate deploy affordances on
        // "has at least one completed version".
        availableVersions,
        // Direct-action entry points for surfaces with dedicated buttons
        // (task page header) instead of the dropdown menu.
        handleDuplicateTask,
        handleDeleteTask,
        deployMenuItem,
        undeployMenuItem,
    };
};

export default useTaskActions;
